import React, { useEffect, useState } from "react";
import StoreLogo from "@/components/storefront/StoreLogo";
import { socialItems } from "@/lib/social";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, Heart, ShoppingBag, User, X, ChevronUp, MapPin, Mail, Truck, ShieldCheck, BadgePercent } from "lucide-react";
import { Reveal } from "@/components/pharmacy/hooks";
import MobileNav from "@/components/storefront/MobileNav";
import { navLinks, footerColumns, footerLines, trustItems, isNavActive } from "@/lib/storenav";

// Look-and-feel tokens of the "Inkwell" books layout: airy white page, coral accent, serif headings.
export const INK = { serif: "'Merriweather', Georgia, serif", ink: "#2a2a2a", grey: "#f5f5f5", cream: "#fdf4e7", line: "#ececec", muted: "#8a8a8a" };

export function InkTitle({ children, center = false, className = "" }) {
  return (
    <div className={`${center ? "text-center" : ""} ${className}`}>
      <h2 className="text-[26px] md:text-[28px] font-bold leading-tight" style={{ fontFamily: INK.serif, color: INK.ink }}>{children}</h2>
      <span className={`block mt-3 h-[2px] w-[54px] ${center ? "mx-auto" : ""}`} style={{ background: "var(--ink-accent, #f2616a)" }} />
    </div>
  );
}

export function InkHeader({ s, cart }) {
  const { store, slug, c } = s;
  const nav = useNavigate();
  const [searching, setSearching] = useState(false);
  const [q, setQ] = useState("");
  const [stuck, setStuck] = useState(false);
  useEffect(() => { const f = () => setStuck(window.scrollY > 40); f(); window.addEventListener("scroll", f, { passive: true }); return () => window.removeEventListener("scroll", f); }, []);
  const search = (e) => { e.preventDefault(); setSearching(false); nav(`/store/${slug}/shop${q ? `?q=${encodeURIComponent(q)}` : ""}`); };
  const links = navLinks(slug);
  const { pathname } = useLocation();
  const Badge = ({ n }) => <span className="absolute -top-2 -right-3 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center" style={{ background: c.primary }}>{n}</span>;
  return (
    <header className="sticky top-0 z-30 bg-white" style={{ boxShadow: stuck ? "0 6px 24px rgba(0,0,0,.07)" : "none", transition: "box-shadow .3s", "--ink-accent": c.primary }}>
      <div className="max-w-6xl mx-auto px-5 h-[88px] flex items-center justify-between gap-6">
        <Link to={`/store/${slug}`} data-testid="storefront-name" className="shrink-0 uppercase font-bold truncate max-w-[300px]" style={{ fontFamily: INK.serif, letterSpacing: ".22em", fontSize: 22, color: INK.ink }}><StoreLogo store={store} height={46} fallback={store.name} /></Link>
        <nav className="hidden lg:flex items-center gap-8 text-[12px] font-semibold uppercase tracking-wide" data-testid="storefront-nav">
          {links.map(([l, to]) => { const on = isNavActive(pathname, slug, to); return (
            <Link key={l} to={to} aria-current={on ? "page" : undefined} className="relative py-1" style={{ color: on ? c.primary : INK.ink }} data-testid={l === "Track Order" ? "storefront-track-link" : undefined}>
              {l}{on && <span className="absolute left-0 right-0 -bottom-0.5 h-px" style={{ background: c.primary }} />}
            </Link>
          ); })}
        </nav>
        <div className="flex items-center gap-5" style={{ color: INK.ink }}>
          <button onClick={() => setSearching((x) => !x)} aria-label="Search" data-testid="ink-search-toggle" className="hover:opacity-60"><Search size={18} /></button>
          <span className="hidden sm:block relative cursor-default" aria-hidden><Heart size={18} /><Badge n={0} /></span>
          <button onClick={() => cart.setOpen(true)} data-testid="storefront-cart-btn" aria-label="Open cart" className="relative hover:opacity-60"><ShoppingBag size={18} /><Badge n={cart.count} /></button>
          <Link to={`/store/${slug}/track`} aria-label="Track order" className="hidden sm:block hover:opacity-60"><User size={18} /></Link>
          <MobileNav slug={slug} color={INK.ink} bg="#fff" accent={c.primary} border={INK.line} />
        </div>
      </div>
      {searching && (
        <form onSubmit={search} className="border-t" style={{ borderColor: INK.line, background: "#fff" }}>
          <div className="max-w-6xl mx-auto px-5 h-14 flex items-center gap-3">
            <Search size={16} style={{ color: INK.muted }} />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search books, authors…" aria-label="Search books" data-testid="ink-search" className="flex-1 outline-none text-[14px] bg-transparent" />
            <button type="button" onClick={() => setSearching(false)} aria-label="Close search"><X size={16} /></button>
          </div>
        </form>
      )}
    </header>
  );
}

function FootHead({ children, c }) {
  return (
    <div className="pb-3 relative border-b" style={{ borderColor: INK.line }}>
      <div className="text-[13px] font-bold uppercase" style={{ fontFamily: INK.serif, color: INK.ink }}>{children}</div>
      <span className="absolute left-0 -bottom-px h-[2px] w-9" style={{ background: c.primary }} />
    </div>
  );
}

export function InkFooter({ s }) {
  const { store, slug, c } = s;
  const [top, setTop] = useState(false);
  useEffect(() => { const f = () => setTop(window.scrollY > 600); window.addEventListener("scroll", f, { passive: true }); return () => window.removeEventListener("scroll", f); }, []);
  const lines = footerLines(store);
  const icons = { money: BadgePercent, verified: ShieldCheck, delivery: Truck };
  // Same structure as every theme: trust strip, store details + the three standard link columns, copyright.
  return (
    <footer data-testid="storefront-footer" style={{ "--ink-accent": c.primary }}>
      <div style={{ background: INK.cream }}>
        <div className="max-w-6xl mx-auto px-5 py-9 grid sm:grid-cols-3 gap-7">
          {trustItems(store.requires_prescription, store.free_shipping_over).map(([key, h, d], k) => { const I = icons[key]; return (
            <Reveal key={h} delay={k * 100} className="flex gap-4"><I size={34} strokeWidth={1.2} className="shrink-0" style={{ color: INK.ink }} /><div><div className="text-[16px] font-bold" style={{ fontFamily: INK.serif }}>{h}</div><p className="text-[12.5px] mt-1 leading-relaxed" style={{ color: "#666" }}>{d}</p></div></Reveal>
          ); })}
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-5 py-12 grid md:grid-cols-5 gap-9">
        <Reveal className="md:col-span-2">
          <StoreLogo store={store} height={48} fallback={<div className="uppercase font-bold" style={{ fontFamily: INK.serif, letterSpacing: ".22em", fontSize: 20, color: INK.ink }}>{store.name}</div>} />
          <p className="text-[13px] mt-4 flex gap-3" style={{ color: "#555" }}><MapPin size={16} className="mt-0.5 shrink-0" style={{ color: c.primary }} />{lines.location}</p>
          <p className="text-[13px] mt-2 flex gap-3" style={{ color: "#555" }}><Mail size={16} className="mt-0.5 shrink-0" style={{ color: c.primary }} />{lines.support}</p>
          <div className="mt-4 flex gap-2.5">{socialItems(s).map((I) => <a key={I.network} href={I.url} target="_blank" rel="noopener noreferrer" aria-label={I.label} title={I.label} data-testid={`footer-social-${I.network}`} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: INK.grey, color: INK.ink }}><I.icon size={14} /></a>)}</div>
        </Reveal>
        {footerColumns(slug).map(([h, links], i) => (
          <Reveal key={h} delay={(i + 1) * 90}>
            <FootHead c={c}>{h}</FootHead>
            <ul className="mt-5 space-y-3 text-[13px]" style={{ color: "#555" }}>{links.map(([l, to]) => <li key={l}><Link to={to} className="rx-link hover:text-black">{l}</Link></li>)}</ul>
          </Reveal>
        ))}
      </div>
      <div className="border-t text-center text-[12px] py-5" style={{ borderColor: INK.line, color: "#666" }}>© 2026 {store.name} · Powered by <Link to="/" className="underline">Sell24x7</Link></div>
      {top && <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} aria-label="Back to top" className="fixed bottom-6 right-6 z-30 w-10 h-10 flex items-center justify-center rx-slide-up text-white" style={{ background: c.primary }}><ChevronUp size={20} /></button>}
    </footer>
  );
}
